<template>
  <content-navigation-wrapper>
    <template #dropdown>
      <base-dropdown
        v-if="menuItems.length"
        id="menu-category-dropdown"
        ref="baseDropdown"
        :options="menuItems"
        :model-value="menuData?.activeMenu?.toString()"
        name="menu-category-dropdown"
        data-test-id="menu-category-dropdown"
        aria-label="Select menu"
        :custom-select="handleMenuSelect"
        :a11y-enabled="false"
      >
        <template
          #dropdown-content="{ focusedOption, handleSelect, handleMouseDown, handleMouseUp }"
        >
          <span
            v-if="menuItems.length && seasonalMenuItems.length"
            class="base-dropdown__dropdown-heading"
          >
            {{ t('menus-standard-menus') }}
          </span>
          <a
            v-for="option in menuItems"
            :id="option.itemId"
            :key="option.itemId"
            :href="getMenuHref(option.value)"
            :aria-selected="menuData?.activeMenu?.toString() === option.value"
            role="option"
            class="base-dropdown__option"
            :class="{
              'base-dropdown__option--hover': isFocusedOption(focusedOption, option.value),
            }"
            :tabindex="-1"
            @click.prevent="handleSelect(option)"
            @mousedown="handleMouseDown"
            @mouseup="handleMouseUp"
          >
            {{ option.text }}
          </a>
          <span v-if="seasonalMenuItems.length" class="base-dropdown__dropdown-heading">
            {{ t('menus-seasonal-menus') }}
          </span>
          <a
            v-for="option in seasonalMenuItems"
            :id="option.itemId"
            :key="option.itemId"
            :href="option.value"
            target="_blank"
            :aria-selected="false"
            role="option"
            class="base-dropdown__option base-dropdown__link"
            :class="{
              'base-dropdown__option--hover': isFocusedOption(focusedOption, option.value),
            }"
            :tabindex="-1"
            @click.prevent="handleSelect(option)"
            @mousedown="handleMouseDown"
            @mouseup="handleMouseUp"
          >
            {{ option.text }}
            <i class="icon icon-Link-External" aria-hidden="true"></i>
          </a>
        </template>
      </base-dropdown>
      <span class="sr-only" aria-live="polite" tabindex="-1">
        {{
          a11yFocusedOption?.text &&
          a11yFocusedOption?.value &&
          `${a11yFocusedOption.text} ${
            menuData?.activeMenu?.toString() === a11yFocusedOption.value
              ? 'selected'
              : 'not selected'
          } ${a11yFocusedValueIndex} of ${allDropdownItems.length}`
        }}
      </span>
    </template>
    <template #filters>
      <button
        class="btn btn--primary btn--icon content-navigation__filters-btn"
        :aria-label="'Filter' + (enabledFiltersNumber ? ` (${enabledFiltersNumber} applied)` : '')"
        :aria-expanded="isFilterBarOpen"
        @click="onFilterBtnClick"
      >
        {{ t('iorder-filters-header-label') }}
        <i class="icon icon-Filters" aria-hidden="true"></i>
      </button>
      <span v-if="enabledFiltersNumber" class="content-navigation__filters-counter">
        {{ enabledFiltersNumber }}
      </span>
    </template>
    <template #subnavigation>
      <base-nav-list
        v-if="submenuItems.length"
        variant="links"
        :underline="false"
        :items="submenuItems"
        :handle-sub-navigation-click="handleSubNavigationClick"
      />
    </template>
  </content-navigation-wrapper>
</template>

<script setup lang="ts">
import type { MenuNavigationProps } from './types';
import type { MenuData, SeasonalMenu, SitecoreField } from '../types';
import type { BaseDropdownOption } from '@/types';
import { computed, useTemplateRef } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import { slugifyFilterValue } from '../../../utils/urlFilters';
import BaseNavList from '../../2_elements/BaseNavList/BaseNavList.vue';
import BaseDropdown from '../../2_elements/BaseDropdown/BaseDropdown.vue';
import ContentNavigationWrapper from '../../2_elements/ContentNavigationWrapper/ContentNavigationWrapper.vue';

const { t } = useI18n();

const props = withDefaults(defineProps<MenuNavigationProps>(), {
  menuData: () => ({}) as MenuData,
  isFilterBarOpen: false,
  seasonalMenus: () => ({}) as SitecoreField<SeasonalMenu[]>,
});

const emit = defineEmits<{
  menuItemClick: [menuId: number];
  menuSubitemClick: [menuId: number];
  openFilterBar: [];
}>();

const route = useRoute();
const routePath = computed(() => route.path);

const baseDropdownRef = useTemplateRef('baseDropdown');

const onFilterBtnClick = () => {
  emit('openFilterBar');
};

const enabledFiltersNumber = computed(
  () =>
    props.menuData?.activeFilters
      ?.filter((f) => f.type === 'filters')
      .flatMap((f) => f.values.filter((v) => v.enabled)).length || 0
);

interface DropdownItem extends BaseDropdownOption {
  itemId: string;
  value: string;
  text: string;
  isLink?: boolean;
}

const seasonalMenuItems = computed<DropdownItem[]>(
  () =>
    props.seasonalMenus?.value?.map((item) => ({
      itemId: `opt-${item.name.replaceAll(/\s+/g, '-')}`,
      value: item.menuLink.href,
      text: item.name,
      isLink: true,
    })) || []
);

const menuItems = computed<DropdownItem[]>(
  () =>
    props.menuData?.menus?.map((item) => ({
      itemId: `opt-${item.id}`,
      value: item.id.toString(),
      text: item.name,
    })) || []
);

const allDropdownItems = computed<DropdownItem[]>(() => [
  ...menuItems.value,
  ...seasonalMenuItems.value,
]);

const a11yFocusedOption = computed((): DropdownItem | null => {
  const focused = baseDropdownRef.value?.focusedOption;
  if (!focused || typeof focused === 'string') return null;
  return focused as DropdownItem;
});

const a11yFocusedValueIndex = computed(
  () =>
    allDropdownItems.value.findIndex((item) => item.itemId === a11yFocusedOption.value?.itemId) + 1
);

const isFocusedOption = (focused: BaseDropdownOption | string | null, value: string): boolean => {
  if (!focused || typeof focused === 'string') return false;
  return focused.value === value;
};

const getMenuHref = (menuId: string): string => {
  const menu = props.menuData?.menus?.find((m) => m.id.toString() === menuId);
  if (!menu) return routePath.value;
  const params = new URLSearchParams({ type: slugifyFilterValue(menu.name) });
  return `${routePath.value}?${params.toString()}`;
};

const handleMenuSelect = (option: BaseDropdownOption | string): void => {
  if (typeof option === 'string') return;
  const item = option as DropdownItem;
  if (item.isLink) {
    if (item.value && !isServer()) window.open(item.value, '_blank');
  } else {
    emit('menuItemClick', +item.value);
  }
};

const submenuItems = computed(() => {
  const activeMenuName = props.menuData?.activeMenuItem?.name;
  return (
    props.menuData?.activeMenuItem?.menuTree?.map((item) => {
      const params = new URLSearchParams({
        type: slugifyFilterValue(activeMenuName || ''),
        section: slugifyFilterValue(item.name),
      });
      return {
        id: item.id.toString(),
        label: item.name.toLowerCase(),
        title: item.name,
        href: `${routePath.value}?${params.toString()}`,
        isActive: item.id === props.menuData?.activeSubmenu,
      };
    }) || []
  );
});

const handleSubNavigationClick = (item: { id: string | number }) => {
  emit('menuSubitemClick', +item.id);
};
</script>
